export const HISTORICAL_ROOT = '/normal-operations/risk-warning/evaluation/historical';
export const HISTORICAL_METHODS = [
  'LAST_VALUE_HOURLY_V1',
  'SEASONAL_24H_V1',
  'LOCAL_LINEAR_HOURLY_V1',
  'RIDGE_LAG_HOURLY_V1',
] as const;
export type HistoricalMethod = (typeof HISTORICAL_METHODS)[number];
export type HistoricalSourceType =
  | 'NOAA_GHCNH_STATION_OBSERVATION'
  | 'SYNTHETIC_OBSERVATIONS'
  | 'METEOROLOGICAL_REANALYSIS'
  | 'UNKNOWN_SOURCE';
export interface HistoricalSource {
  stationId: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  url: string;
  originalHash: string;
  bytes: number;
  downloadedRows: number;
  qualifiedRows: number;
  missingRows: number;
}
export interface HistoricalDataset {
  datasetId: string;
  datasetRevision: string;
  sourceType: HistoricalSourceType;
  license: string;
  doi: string;
  accessedAt: string;
  snapshotHash: string;
  start: string;
  end: string;
  resolution: string;
  metrics: string[];
  sources: HistoricalSource[];
  qualityNotes: string[];
}
export interface HistoricalSplit {
  trainStart: string;
  trainEnd: string;
  validationStart: string;
  validationEnd: string;
  heldoutStart: string;
  heldoutEnd: string;
  embargoHours: number;
}
export interface HistoricalFreeze {
  modelFreezeSHA: string;
  algorithmSHA: string;
  trainingHash: string;
  parametersJson: string;
  frozenAt: string;
}
export interface HistoricalGroup {
  stationId: string;
  metric: string;
  unit: string;
  horizonHours: number;
  method: HistoricalMethod;
  split: string;
  totalOrigins: number;
  generated: number;
  evaluable: number;
  pairedCount: number;
  mae: number | null;
  rmse: number | null;
  bias: number | null;
  coverage: number | null;
  pairedMAE: number | null;
  pairedRMSE: number | null;
  baselineMAEDelta: number | null;
  baselineRMSEDelta: number | null;
  unavailableReasons: { reason: string; count: number }[];
}
export interface HistoricalRow {
  stationId: string;
  metric: string;
  unit: string;
  horizonHours: number;
  method: HistoricalMethod;
  split: string;
  origin: string;
  targetAt: string;
  inputStart: string | null;
  inputEnd: string | null;
  predicted: number | null;
  actual: number | null;
  status: string;
  reason: string | null;
  paired: boolean;
}
export interface HistoricalRun {
  schemaVersion: string;
  runId: string;
  status: string;
  mode: string;
  createdAt: string;
  dataset: HistoricalDataset;
  split: HistoricalSplit;
  freeze: HistoricalFreeze;
  horizonsHours: number[];
  methods: HistoricalMethod[];
  predictiveUtility: string;
  groups: HistoricalGroup[];
  errorCases: HistoricalRow[];
  rowsHash: string;
  rowCount: number;
  limitations: string[];
}
export interface HistoricalPageResult<T> {
  items: T[];
  total: number;
  offset: number;
  limit: number;
}
export interface HistoricalFilter {
  stationId: string;
  metric: string;
  method: string;
  horizonHours: string;
  from: string;
  to: string;
  offset: number;
  limit: number;
}
export const INITIAL_HISTORICAL_FILTER: HistoricalFilter = {
  stationId: '',
  metric: '',
  method: '',
  horizonHours: '',
  from: '',
  to: '',
  offset: 0,
  limit: 20,
};
export interface HistoricalPort {
  runs(filter: HistoricalFilter, signal: AbortSignal): Promise<HistoricalPageResult<HistoricalRun>>;
  run(id: string, signal: AbortSignal): Promise<HistoricalRun>;
  rows(
    id: string,
    filter: HistoricalFilter,
    signal: AbortSignal,
  ): Promise<HistoricalPageResult<HistoricalRow>>;
}
export function historicalSourceLabel(source: HistoricalSourceType): string {
  return {
    NOAA_GHCNH_STATION_OBSERVATION: '真实历史气象站观测 · NOAA GHCNh',
    SYNTHETIC_OBSERVATIONS: '合成观测 · 仅实验',
    METEOROLOGICAL_REANALYSIS: '气象再分析 · 网格重建',
    UNKNOWN_SOURCE: '来源未知 · 不可视为真实验证',
  }[source];
}
export const validHistoricalRunId = (id: string) => /^historical-[a-f0-9]{64}$/.test(id);
export function historicalHref(id = '', filter = INITIAL_HISTORICAL_FILTER, areaId = ''): string {
  const p = new URLSearchParams();
  for (const key of ['stationId', 'metric', 'method', 'horizonHours', 'from', 'to'] as const)
    if (filter[key]) p.set(key, filter[key]);
  if (filter.offset) p.set('offset', String(filter.offset));
  if (areaId) p.set('areaId', areaId);
  return `${HISTORICAL_ROOT}${id ? `/runs/${encodeURIComponent(id)}` : ''}${p.size ? `?${p}` : ''}`;
}
export function parseHistoricalLocation(
  path: string,
  search: string,
): { id: string | null; filter: HistoricalFilter; areaId: string; valid: boolean } {
  const p = new URLSearchParams(search);
  const filter = { ...INITIAL_HISTORICAL_FILTER };
  for (const key of ['stationId', 'metric', 'method', 'horizonHours', 'from', 'to'] as const)
    filter[key] = p.get(key) ?? '';
  filter.offset = Number(p.get('offset') ?? 0);
  const suffix = path.replace(/\/$/, '').slice(HISTORICAL_ROOT.length);
  const id = suffix.startsWith('/runs/') ? suffix.slice(6) : null;
  const areaId = p.get('areaId') ?? '';
  return {
    id,
    filter,
    areaId,
    valid:
      path.startsWith(HISTORICAL_ROOT) &&
      (suffix === '' || (id !== null && validHistoricalRunId(id))) &&
      (!areaId || /^[a-z][a-z0-9-]{0,63}$/.test(areaId)) &&
      Number.isSafeInteger(filter.offset) &&
      filter.offset >= 0 &&
      filter.offset <= 1000000,
  };
}
